import { Injectable } from '@angular/core'
import { FeathersService } from './feathers.service'
import { AssistantAvailability } from '../models/assistantAvailability'
import { RecommendedAssistantsFilter } from '../features/assistance/assistance-editor/recommended-assistants-filter'
import { map, switchMap, tap } from 'rxjs/operators'
import { combineLatest, Observable } from 'rxjs'
import { Assistant } from '../models/assistant'
import { DateRange, DateUtils } from './date-utils.service'
import { AssistantService } from './domain/assistant/assistant.service'
import {
  AssistantAvailabilitySelectItem,
  AssistantAvailabilitySelectItemFlag,
} from '../features/assistant/assistant-select/components/assistant-select/AssistantAvailabilitySelectItem'
import { subDays } from 'date-fns'
import { AssistantAvailabilitiesOccupationService } from './domain/assistant-availabilities/assistant-availabilities-occupation.service'
import { ExchangeService } from './exchange.service'

@Injectable({
  providedIn: 'root',
})
export class AssistantAvailabilitiesRecommendationService {
  private TAG = 'AssistantAvailabilitiesRecommendationService'

  constructor(
    private feathers: FeathersService,
    private assistantService: AssistantService,
    private aaOccupationService: AssistantAvailabilitiesOccupationService,
    private exchangeService: ExchangeService,
  ) {}

  public getAvailabilitiesInRange(range: DateRange, assistantsToDisable: Assistant[] = []) {
    const assistantIdsToDisable = assistantsToDisable.map((a) => a.id)

    const query = {
      queryType: 'assistanceAssistantSelect',
      ...(range.from && { dt_start: { $lte: range.from } }),
      ...(range.to && {
        $or: [
          { dt_end: { $gte: range.to } },
          {
            rrule: { $ne: null },
            recurrenceEnd: { $gte: subDays(range.to, 1) },
          },
          { rrule: { $ne: null }, recurrenceEnd: null },
        ],
      }),
    }

    const availabilities$ = this.feathers.find(AssistantAvailability, query).pipe(
      map((aa: AssistantAvailability[]) => AssistantAvailability.expandAvailabilitiesWhichEngulfRange(aa, range)),
      map((aa) => aa.filter((a) => !assistantIdsToDisable.includes(a.assistantId))),
      switchMap((expandedAA) => this.aaOccupationService.addOccupationsAndVacantTimesMulti(expandedAA)),
      // map(aaWithOccupation => aaWithOccupation.filter(a => !a.isOccupiedAt(range))),
    )
    const exchangeAvailabilities$ = this.getActiveExchangeAvailabilitiesEmbracingRange(range).pipe(
      map((aa) => aa.filter((a) => !assistantIdsToDisable.includes(a.assistantId))),
    )

    return combineLatest([availabilities$, exchangeAvailabilities$]).pipe(
      map(([availabilities, exchangeAvailabilities]) => {
        const availabilitySelectItems = availabilities
          .filter(
            (aa) =>
              // Exclude availabilities that already contain the same assistant in exchange availabilities
              !exchangeAvailabilities.find((ea) => ea.assistantId === aa.assistantId),
          )
          .map(
            (availability) =>
              new AssistantAvailabilitySelectItem({
                availability,
                enabled: true,
              }),
          )
        const exchangeAvailabilitySelectItems = exchangeAvailabilities
          .filter(
            (ea) =>
              // If there are more exchange availabilities with the same assistant, leave only the first one.
              exchangeAvailabilities.find((ea2) => ea2.assistantId === ea.assistantId) === ea,
          )
          .map(
            (availability) =>
              new AssistantAvailabilitySelectItem({
                availability,
                enabled: true,
                flags: [AssistantAvailabilitySelectItemFlag.IsExchangeAssistance],
              }),
          )
        return [...availabilitySelectItems, ...exchangeAvailabilitySelectItems]
      }),
      tap((e) => console.log(`${this.TAG}#getAvailabilitiesInRange`, e)),
    )
  }

  filterItems(filter: RecommendedAssistantsFilter, items: AssistantAvailabilitySelectItem[]) {
    return items.filter((i) => {
      return (
        this.filterAvailabilityByAssistanceParameters(filter, i.availability) &&
        this.filterAvailabilityIfOccupied(filter, i.availability)
      )
    })
  }

  private filterAvailabilityByAssistanceParameters(
    filter: RecommendedAssistantsFilter,
    availability: AssistantAvailability,
  ) {
    // console.log(availability)
    const deactivatedAssistantCheck = !availability.assistant.isInactive()

    const strongCheck = !filter.values.needsStrongAssistant || availability.assistant.isStrong
    const advancedCheck = !filter.values.needsAdvancedAssistant || availability.assistant.isAdvanced
    const genderCheck =
      !filter.values.preferredGenderId || filter.values.preferredGenderId === availability.assistant.genderId

    // Tests for date selection
    //
    // Assistant select reqs:
    //
    // Data:
    //
    // Non-recurring Availability
    // (1) 3.1. 10:00 - 3.1. 15:00  / 6.2.
    //
    // Recurring Availability
    // (2a) 5.1. 10:00 - 5.1. 12:00 / 7.2.
    // (2b) 6.1. 10:00 - 6.1. 12:00
    // (2c) 7.1. 10:00 - 7.1. 12:00
    //
    //
    // Test cases:
    //
    // TimeFrom: 3.1. 10:00
    // TimeTo: 3.1. 14:00
    // Expected result: (1)
    //
    // TimeFrom: 5.1. 10:00
    // TimeTo: 5.1. 11:00
    // Expected result: (2a)
    //
    // TimeFrom: 5.1. 10:00
    // TimeTo: 5.1. 14:00
    // Expected result: (---)
    //
    // TimeFrom: 5.1. 11:00
    // TimeTo: 5.1. 14:00
    // Expected result: (---)
    //
    // TimeFrom: 1.1. 10:00
    // TimeTo: ---
    // Expected result: (1, 2a, 2b, 2c)
    // Reason: Whichever Assistant is available on TimeFrom and ends later, ie. has Assistant.timeFrom <= filter.TimeFrom && Assistant.TimeTo > filter.TimeFrom
    //
    // TimeFrom: ---
    // TimeTo: 4.1. 10:00
    // Expected result: ()
    // Reason: Whichever Assistant is available on TimeTo and starts earlier, ie. has Assistant.timeTo >= filter.TimeTo && Assistant.TimeFrom < filter.TimeTo

    // check na datum jeste znova kvuli rekurencim
    let dateFromCheck: boolean = true
    let dateToCheck: boolean = true

    const assistantStart: Date = availability.dt_start ? availability.getDateFrom() : null
    const assistantEnd: Date = availability.dt_end ? availability.getDateTo() : null
    const filterStart: Date = new Date(filter.values.dateFrom)
    const filterEnd: Date = new Date(filter.values.dateTo)

    if (assistantStart && assistantEnd) {
      if (filter.values.dateFrom && filter.values.dateTo) {
        dateFromCheck = assistantStart <= filterStart
        dateToCheck = assistantEnd >= filterEnd
      } else if (filter.values.dateFrom) {
        dateFromCheck = assistantStart <= filterStart && assistantEnd > filterStart
      } else if (filter.values.dateTo) {
        dateToCheck = assistantEnd >= filterEnd && assistantStart < filterEnd
      }
    }

    const dateCheck = dateFromCheck && dateToCheck

    return strongCheck && advancedCheck && genderCheck && dateCheck && deactivatedAssistantCheck
  }

  private filterAvailabilityIfOccupied(filter: RecommendedAssistantsFilter, availability: AssistantAvailability) {
    if (!availability.assistanceOccupations) {
      // console.log(this.TAG, 'filterAvailabilityIfOccupied no assistanceOccupations', availability)
      return true
    }
    // if availability is occupied on the given date, filter it out
    const overlap = DateUtils.dateRangesAnyOverlap([filter.getDateRange()], availability.getOccupiedTimes())
    // console.log(this.TAG, 'should filter out occupied availability', overlap)
    return !overlap
  }

  getAvailabilitiesForAllActiveAssistants(
    range: DateRange,
    assistantsToDisable: Assistant[],
  ): Observable<AssistantAvailabilitySelectItem[]> {
    return this.assistantService.getAllActiveWithUserWithCaching().pipe(
      map((activeAssistants) => {
        return activeAssistants.map((activeAssistant) => {
          const availability = new AssistantAvailability()
          availability.assistant = activeAssistant
          availability.assistantId = activeAssistant.id

          const disabled = assistantsToDisable.some((aToDisable) => activeAssistant.id === aToDisable.id)

          const item = new AssistantAvailabilitySelectItem({
            availability,
            enabled: !disabled,
          })

          return item
        })
      }),
    )
  }

  getActiveExchangeAvailabilitiesEmbracingRange(range: DateRange): Observable<AssistantAvailability[]> {
    return this.exchangeService.getActiveEnrollmentsEmbracingRange(range).pipe(
      tap((enrollments) => {
        console.log(this.TAG, 'getActiveExchangeAvailabilitiesEmbracingRange got enrollments', enrollments)
      }),
      map((enrollments) =>
        enrollments.map((enrollment) => {
          const availability = new AssistantAvailability()
          availability.assistant = enrollment.assistant
          availability.assistantId = enrollment.assistant.id
          return availability
        }),
      ),
    )
  }
}
